'use client'

import React, { useState, useEffect } from 'react'
import { formatPhone, isValidPhone } from '@/lib/phone'

interface PhoneInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  label?: string
  error?: string
  fullWidth?: boolean
  value: string
  onChange: (value: string) => void
}

export function PhoneInput({
  label,
  error,
  fullWidth = false,
  className = '',
  value,
  onChange,
  ...props
}: PhoneInputProps) {
  const widthStyle = fullWidth ? 'w-full' : ''
  const isReadOnly = props.readOnly || props.disabled
  const [displayValue, setDisplayValue] = useState(value)

  // Sync display value with prop value
  useEffect(() => {
    setDisplayValue(value)
  }, [value])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setDisplayValue(newValue)
    onChange(newValue)
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const trimmedValue = displayValue.trim()

    if (trimmedValue && isValidPhone(trimmedValue)) {
      const formatted = formatPhone(trimmedValue)
      setDisplayValue(formatted)
      onChange(formatted)
    }

    // Call original onBlur if provided
    if (props.onBlur) {
      props.onBlur(e)
    }
  }

  return (
    <div data-component-name="PhoneInput" className={`${widthStyle}`}>
      {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
      <input
        type="tel"
        className={`
          px-4 py-3
          border border-gray-300
          rounded-lg
          focus:outline-none
          focus:ring-2
          focus:ring-primary
          focus:border-transparent
          transition-all duration-200
          ${error ? 'border-danger' : ''}
          ${isReadOnly ? 'bg-gray-50 text-gray-600 cursor-not-allowed' : ''}
          ${widthStyle}
          ${className}
        `}
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        {...props}
      />
      {error && <p className="mt-1 text-sm text-danger">{error}</p>}
    </div>
  )
}

// Export utility function for validation (re-export from shared lib)
export { isValidPhone as isValidPhoneNumber } from '@/lib/phone'
